import React from 'react';
import { useEntity } from '@backstage/plugin-catalog-react';
import { useApi, fetchApiRef, configApiRef } from '@backstage/core-plugin-api';
import { EmptyState } from '@backstage/core-components';
import {
  Box,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  Paper,
  Collapse,
  CircularProgress,
  Button,
  Switch,
  FormControlLabel,
  LinearProgress,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import RefreshIcon from '@material-ui/icons/Refresh';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ErrorIcon from '@material-ui/icons/Error';
import WarningIcon from '@material-ui/icons/Warning';
import HelpIcon from '@material-ui/icons/Help';
import PlayArrowIcon from '@material-ui/icons/PlayArrow';
import SyncIcon from '@material-ui/icons/Sync';
import FiberManualRecordIcon from '@material-ui/icons/FiberManualRecord';
import ScheduleIcon from '@material-ui/icons/Schedule';
import StorageIcon from '@material-ui/icons/Storage';
import CloudIcon from '@material-ui/icons/Cloud';
import SettingsInputComponentIcon from '@material-ui/icons/SettingsInputComponent';
import LanguageIcon from '@material-ui/icons/Language';
import LockIcon from '@material-ui/icons/Lock';
import MemoryIcon from '@material-ui/icons/Memory';

/* ─── Types ─── */

interface ArgoApp {
  metadata: {
    name: string;
    namespace: string;
  };
  spec: {
    source?: { repoURL: string; path: string; targetRevision: string };
    destination?: { server: string; namespace: string };
  };
  status: {
    sync: { status: string; revision: string };
    health: { status: string; message?: string };
    operationState?: {
      phase: string;
      message?: string;
      startedAt?: string;
      finishedAt?: string;
      syncResult?: {
        resources?: Array<{
          group: string;
          kind: string;
          namespace: string;
          name: string;
          status: string;
          message: string;
          hookPhase?: string;
        }>;
      };
    };
    resources?: Array<{
      group: string;
      kind: string;
      namespace: string;
      name: string;
      status: string;
      health?: { status: string; message?: string };
      version: string;
    }>;
    history?: Array<{
      id: number;
      revision: string;
      deployedAt: string;
      source?: { repoURL: string; path: string };
    }>;
    conditions?: Array<{
      type: string;
      message: string;
      lastTransitionTime?: string;
    }>;
  };
}

interface ResourceTreeNode {
  group: string;
  kind: string;
  namespace: string;
  name: string;
  version: string;
  health?: { status: string; message?: string };
  parentRefs?: Array<{ group: string; kind: string; name: string; namespace: string }>;
  info?: Array<{ name: string; value: string }>;
  createdAt?: string;
}

/* ─── Styles ─── */

const useStyles = makeStyles(theme => ({
  toolbar: {
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'center',
    flexWrap: 'wrap',
    marginBottom: theme.spacing(1.5),
  },
  statusCard: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(1.5),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
  },
  statusGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  infoItem: {
    display: 'flex',
    flexDirection: 'column' as const,
    marginRight: theme.spacing(3),
  },
  infoLabel: {
    fontSize: '0.7rem',
    color: theme.palette.text.hint,
    textTransform: 'uppercase' as const,
    fontWeight: 600,
  },
  infoValue: {
    fontSize: '0.85rem',
    fontFamily: 'monospace',
  },
  resourceRow: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(0.75, 1.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  resourceKind: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    minWidth: 140,
    fontFamily: 'monospace',
  },
  resourceName: {
    fontSize: '0.85rem',
    fontWeight: 500,
    flex: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
  },
  treeIndent: {
    borderLeft: `2px solid ${theme.palette.divider}`,
    marginLeft: theme.spacing(2),
    paddingLeft: theme.spacing(1),
  },
  historyRow: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(0.75, 1.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
    gap: theme.spacing(1),
  },
  historyId: {
    fontFamily: 'monospace',
    fontSize: '0.8rem',
    fontWeight: 600,
    color: theme.palette.primary.main,
    minWidth: 40,
  },
  historyRev: {
    fontFamily: 'monospace',
    fontSize: '0.75rem',
    color: theme.palette.text.hint,
    minWidth: 80,
  },
  historyTime: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(1, 1.5),
    backgroundColor: theme.palette.type === 'dark' ? '#1a1a2e' : '#f5f5f5',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  sectionTitle: {
    fontSize: '0.85rem',
    fontWeight: 600,
  },
  conditionRow: {
    padding: theme.spacing(0.5, 1.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
    fontSize: '0.8rem',
  },
  statusBar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(0.5, 1),
    backgroundColor: theme.palette.type === 'dark' ? '#252526' : '#f3f3f3',
    borderRadius: '0 0 4px 4px',
    fontSize: 12,
  },
}));

/* ─── Constants ─── */

const PROXY_BASE = '/api/proxy/argocd/api';

/* ─── Helpers ─── */

function healthIcon(status: string, size = 16) {
  switch (status) {
    case 'Healthy':
      return <CheckCircleIcon style={{ fontSize: size, color: '#4caf50' }} />;
    case 'Degraded':
      return <ErrorIcon style={{ fontSize: size, color: '#f44336' }} />;
    case 'Progressing':
      return <SyncIcon style={{ fontSize: size, color: '#2196f3' }} />;
    case 'Suspended':
      return <ScheduleIcon style={{ fontSize: size, color: '#ff9800' }} />;
    case 'Missing':
      return <HelpIcon style={{ fontSize: size, color: '#9e9e9e' }} />;
    default:
      return <FiberManualRecordIcon style={{ fontSize: size, color: '#bdbdbd' }} />;
  }
}

function syncIcon(status: string, size = 16) {
  switch (status) {
    case 'Synced':
      return <CheckCircleIcon style={{ fontSize: size, color: '#4caf50' }} />;
    case 'OutOfSync':
      return <WarningIcon style={{ fontSize: size, color: '#ff9800' }} />;
    default:
      return <HelpIcon style={{ fontSize: size, color: '#9e9e9e' }} />;
  }
}

function healthColor(status: string): string {
  switch (status) {
    case 'Healthy': return '#4caf50';
    case 'Degraded': return '#f44336';
    case 'Progressing': return '#2196f3';
    case 'Suspended': return '#ff9800';
    default: return '#9e9e9e';
  }
}

function syncColor(status: string): string {
  switch (status) {
    case 'Synced': return '#4caf50';
    case 'OutOfSync': return '#ff9800';
    default: return '#9e9e9e';
  }
}

function kindIcon(kind: string) {
  const size = 16;
  const color = '#757575';
  switch (kind) {
    case 'Deployment': case 'StatefulSet': case 'DaemonSet': case 'ReplicaSet':
      return <MemoryIcon style={{ fontSize: size, color }} />;
    case 'Service': case 'Endpoints':
      return <SettingsInputComponentIcon style={{ fontSize: size, color }} />;
    case 'Ingress':
      return <LanguageIcon style={{ fontSize: size, color }} />;
    case 'Pod':
      return <CloudIcon style={{ fontSize: size, color }} />;
    case 'PersistentVolumeClaim':
      return <StorageIcon style={{ fontSize: size, color }} />;
    case 'Secret': case 'ConfigMap':
      return <LockIcon style={{ fontSize: size, color }} />;
    default:
      return <FiberManualRecordIcon style={{ fontSize: size, color }} />;
  }
}

function formatTimeAgo(isoStr: string): string {
  if (!isoStr) return '-';
  const d = new Date(isoStr);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return '방금';
  if (diffMin < 60) return `${diffMin}분 전`;
  const diffHour = Math.floor(diffMin / 60);
  if (diffHour < 24) return `${diffHour}시간 전`;
  const diffDay = Math.floor(diffHour / 24);
  if (diffDay < 7) return `${diffDay}일 전`;
  return d.toLocaleDateString('ko-KR', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function formatTime(isoStr: string): string {
  if (!isoStr) return '-';
  const d = new Date(isoStr);
  return d.toLocaleString('ko-KR', {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
}

/* ─── Resource Tree Builder ─── */

interface TreeItem {
  node: ResourceTreeNode;
  children: TreeItem[];
}

function buildTree(nodes: ResourceTreeNode[]): TreeItem[] {
  const nodeMap = new Map<string, TreeItem>();
  const roots: TreeItem[] = [];

  for (const node of nodes) {
    const key = `${node.group}/${node.kind}/${node.namespace}/${node.name}`;
    nodeMap.set(key, { node, children: [] });
  }

  for (const node of nodes) {
    const key = `${node.group}/${node.kind}/${node.namespace}/${node.name}`;
    const item = nodeMap.get(key)!;
    const parentRef = (node.parentRefs || [])[0];
    if (parentRef) {
      const parentKey = `${parentRef.group}/${parentRef.kind}/${parentRef.namespace}/${parentRef.name}`;
      const parent = nodeMap.get(parentKey);
      if (parent) {
        parent.children.push(item);
        continue;
      }
    }
    roots.push(item);
  }

  return roots;
}

/* ─── Sub-components ─── */

const ResourceTreeView: React.FC<{
  items: TreeItem[];
  depth?: number;
}> = ({ items, depth = 0 }) => {
  const classes = useStyles();

  // Sort: Deployment > Service > Ingress > others, then by name
  const sorted = [...items].sort((a, b) => {
    const kindOrder: Record<string, number> = {
      Deployment: 0, StatefulSet: 1, DaemonSet: 2, Service: 3,
      Ingress: 4, PersistentVolumeClaim: 5, ConfigMap: 6, Pod: 10, ReplicaSet: 11,
    };
    const aOrder = kindOrder[a.node.kind] ?? 8;
    const bOrder = kindOrder[b.node.kind] ?? 8;
    if (aOrder !== bOrder) return aOrder - bOrder;
    return a.node.name.localeCompare(b.node.name);
  });

  return (
    <Box className={depth > 0 ? classes.treeIndent : undefined}>
      {sorted.map(item => (
        <ResourceNodeRow key={`${item.node.kind}-${item.node.name}`} item={item} depth={depth} />
      ))}
    </Box>
  );
};

const ResourceNodeRow: React.FC<{ item: TreeItem; depth: number }> = ({ item, depth }) => {
  const classes = useStyles();
  const [expanded, setExpanded] = React.useState(depth < 1);
  const { node, children } = item;
  const hasChildren = children.length > 0;
  const health = node.health?.status || '';

  // Skip old ReplicaSets with 0 replicas (info field)
  const replicas = node.info?.find(i => i.name === 'Revision:');
  if (node.kind === 'ReplicaSet' && !replicas && children.length === 0) return null;

  return (
    <>
      <Box
        className={classes.resourceRow}
        onClick={() => hasChildren && setExpanded(!expanded)}
        style={{ paddingLeft: 12 + depth * 8, cursor: hasChildren ? 'pointer' : 'default' }}
      >
        {kindIcon(node.kind)}
        <Typography className={classes.resourceKind} style={{ marginLeft: 6 }}>
          {node.kind}
        </Typography>
        <Typography className={classes.resourceName}>
          {node.name}
        </Typography>
        {health && (
          <Tooltip title={node.health?.message || health}>
            <Box display="flex" alignItems="center" style={{ gap: 4, marginRight: 8 }}>
              {healthIcon(health, 14)}
              <Typography variant="caption" style={{ color: healthColor(health) }}>
                {health}
              </Typography>
            </Box>
          </Tooltip>
        )}
        {hasChildren && (
          <IconButton size="small" style={{ padding: 2 }}>
            {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          </IconButton>
        )}
      </Box>
      {hasChildren && (
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <ResourceTreeView items={children} depth={depth + 1} />
        </Collapse>
      )}
    </>
  );
};

/* ─── Main Component ─── */

export const ArgoCdContent = () => {
  const classes = useStyles();
  const { entity } = useEntity();
  const fetchApi = useApi(fetchApiRef);
  const configApi = useApi(configApiRef);

  const appName = entity?.metadata?.annotations?.['argocd/app-name'] ?? '';
  const argoBaseUrl =
    configApi.getOptionalString('argocd.baseUrl') ??
    'https://argocd.arcus-platform.com';

  /* ─── State ─── */
  const [app, setApp] = React.useState<ArgoApp | null>(null);
  const [treeNodes, setTreeNodes] = React.useState<ResourceTreeNode[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [autoRefresh, setAutoRefresh] = React.useState(false);
  const [showResources, setShowResources] = React.useState(true);
  const [showHistory, setShowHistory] = React.useState(false);
  const [showConditions, setShowConditions] = React.useState(false);
  const [syncing, setSyncing] = React.useState(false);

  /* ─── API helpers ─── */
  const argoGet = React.useCallback(
    async (path: string) => {
      const resp = await fetchApi.fetch(`${PROXY_BASE}${path}`);
      if (!resp.ok) throw new Error(`ArgoCD API ${resp.status}`);
      return resp.json();
    },
    [fetchApi],
  );

  const argoPost = React.useCallback(
    async (path: string, body?: object) => {
      const resp = await fetchApi.fetch(`${PROXY_BASE}${path}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: body ? JSON.stringify(body) : undefined,
      });
      if (!resp.ok) throw new Error(`ArgoCD API ${resp.status}`);
      return resp.json();
    },
    [fetchApi],
  );

  /* ─── Fetch app + resource tree ─── */
  const fetchApp = React.useCallback(async () => {
    if (!appName) return;
    setLoading(true);
    setError(null);
    try {
      const [appData, treeData] = await Promise.all([
        argoGet(`/v1/applications/${appName}`),
        argoGet(`/v1/applications/${appName}/resource-tree`).catch(() => ({ nodes: [] })),
      ]);
      setApp(appData as ArgoApp);
      setTreeNodes((treeData as { nodes: ResourceTreeNode[] }).nodes || []);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [appName, argoGet]);

  React.useEffect(() => {
    fetchApp();
  }, [fetchApp]);

  // Auto-refresh
  React.useEffect(() => {
    if (!autoRefresh) return undefined;
    const interval = setInterval(fetchApp, 15000);
    return () => clearInterval(interval);
  }, [autoRefresh, fetchApp]);

  /* ─── Manual Sync ─── */
  const handleSync = React.useCallback(async () => {
    if (!appName) return;
    setSyncing(true);
    try {
      await argoPost(`/v1/applications/${appName}/sync`, {
        prune: false,
      });
      // Refresh after sync trigger
      setTimeout(fetchApp, 2000);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSyncing(false);
    }
  }, [appName, argoPost, fetchApp]);

  /* ─── Build tree ─── */
  const tree = React.useMemo(() => buildTree(treeNodes), [treeNodes]);

  /* ─── Early returns ─── */
  if (!appName) {
    return (
      <EmptyState
        title="ArgoCD 정보 없음"
        missing="info"
        description="argocd/app-name 어노테이션이 필요합니다."
      />
    );
  }

  const syncStatus = app?.status?.sync?.status ?? '';
  const healthStatus = app?.status?.health?.status ?? '';
  const revision = app?.status?.sync?.revision ?? '';
  const opState = app?.status?.operationState;
  const history = app?.status?.history ?? [];
  const conditions = app?.status?.conditions ?? [];
  const resources = app?.status?.resources ?? [];
  const destNs = app?.spec?.destination?.namespace ?? '';

  return (
    <Box>
      {/* ─── Toolbar ─── */}
      <Box className={classes.toolbar}>
        <FormControlLabel
          control={
            <Switch
              checked={autoRefresh}
              onChange={e => setAutoRefresh(e.target.checked)}
              size="small"
              color="primary"
            />
          }
          label={<Typography variant="caption">Auto (15s)</Typography>}
        />

        <Tooltip title="새로고침">
          <IconButton size="small" onClick={fetchApp} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>

        <Tooltip title="Manual Sync 트리거">
          <span>
            <Button
              size="small"
              variant="outlined"
              startIcon={syncing ? <CircularProgress size={14} /> : <SyncIcon />}
              onClick={handleSync}
              disabled={syncing}
            >
              Sync
            </Button>
          </span>
        </Tooltip>

        <Tooltip title="ArgoCD에서 보기">
          <IconButton
            size="small"
            onClick={() => window.open(`${argoBaseUrl}/applications/${appName}`, '_blank')}
          >
            <OpenInNewIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {/* ─── Error ─── */}
      {error && (
        <Paper style={{ padding: 8, marginBottom: 8, backgroundColor: '#ffebee' }}>
          <Typography color="error" variant="body2">{error}</Typography>
        </Paper>
      )}

      {/* ─── Loading ─── */}
      {loading && !app && (
        <Box display="flex" alignItems="center" justifyContent="center" py={4}>
          <CircularProgress size={24} />
          <Typography variant="body2" style={{ marginLeft: 8 }}>
            Application 조회 중...
          </Typography>
        </Box>
      )}

      {loading && app && <LinearProgress />}

      {/* ─── Status Card ─── */}
      {app && (
        <Paper variant="outlined" className={classes.statusCard}>
          <Box className={classes.statusGroup}>
            <Box className={classes.infoItem}>
              <Typography className={classes.infoLabel}>Application</Typography>
              <Typography className={classes.infoValue} style={{ fontWeight: 600 }}>
                {app.metadata.name}
              </Typography>
            </Box>
            <Box className={classes.infoItem}>
              <Typography className={classes.infoLabel}>Namespace</Typography>
              <Typography className={classes.infoValue}>{destNs}</Typography>
            </Box>
            <Box className={classes.infoItem}>
              <Typography className={classes.infoLabel}>Revision</Typography>
              <Typography className={classes.infoValue}>{revision.substring(0, 8)}</Typography>
            </Box>
            {opState?.finishedAt && (
              <Box className={classes.infoItem}>
                <Typography className={classes.infoLabel}>Last Sync</Typography>
                <Typography className={classes.infoValue}>
                  {formatTimeAgo(opState.finishedAt)}
                </Typography>
              </Box>
            )}
          </Box>

          <Box display="flex" alignItems="center" style={{ gap: 8 }}>
            <Tooltip title={`Sync: ${syncStatus}`}>
              <Chip
                icon={syncIcon(syncStatus, 14)}
                label={syncStatus}
                size="small"
                style={{
                  backgroundColor: syncColor(syncStatus),
                  color: '#fff',
                  fontWeight: 600,
                }}
              />
            </Tooltip>
            <Tooltip title={`Health: ${healthStatus}${app.status.health.message ? ` — ${app.status.health.message}` : ''}`}>
              <Chip
                icon={healthIcon(healthStatus, 14)}
                label={healthStatus}
                size="small"
                style={{
                  backgroundColor: healthColor(healthStatus),
                  color: '#fff',
                  fontWeight: 600,
                }}
              />
            </Tooltip>
            {opState && opState.phase !== 'Succeeded' && (
              <Chip
                label={opState.phase}
                size="small"
                variant="outlined"
                style={{ fontWeight: 600 }}
              />
            )}
          </Box>
        </Paper>
      )}

      {/* ─── Conditions (warnings) ─── */}
      {conditions.length > 0 && (
        <Paper variant="outlined" style={{ marginBottom: 12 }}>
          <Box
            className={classes.sectionHeader}
            onClick={() => setShowConditions(!showConditions)}
          >
            <Box display="flex" alignItems="center" style={{ gap: 8 }}>
              <WarningIcon style={{ fontSize: 16, color: '#ff9800' }} />
              <Typography className={classes.sectionTitle}>
                Conditions ({conditions.length})
              </Typography>
            </Box>
            {showConditions ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </Box>
          <Collapse in={showConditions} timeout="auto" unmountOnExit>
            {conditions.map((c, i) => (
              <Box key={i} className={classes.conditionRow}>
                <Typography variant="caption" color="textSecondary">{c.type}</Typography>
                <Typography variant="body2">{c.message}</Typography>
              </Box>
            ))}
          </Collapse>
        </Paper>
      )}

      {/* ─── Resource Tree ─── */}
      <Paper variant="outlined" style={{ marginBottom: 12 }}>
        <Box
          className={classes.sectionHeader}
          onClick={() => setShowResources(!showResources)}
        >
          <Typography className={classes.sectionTitle}>
            리소스 ({resources.length}개)
          </Typography>
          {showResources ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </Box>
        <Collapse in={showResources} timeout="auto" unmountOnExit>
          {tree.length > 0 ? (
            <ResourceTreeView items={tree} />
          ) : (
            <Box p={2}>
              <Typography variant="body2" color="textSecondary">
                리소스 트리를 불러오는 중...
              </Typography>
            </Box>
          )}
        </Collapse>
      </Paper>

      {/* ─── Sync History ─── */}
      <Paper variant="outlined" style={{ marginBottom: 12 }}>
        <Box
          className={classes.sectionHeader}
          onClick={() => setShowHistory(!showHistory)}
        >
          <Typography className={classes.sectionTitle}>
            배포 히스토리 ({history.length}건)
          </Typography>
          {showHistory ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </Box>
        <Collapse in={showHistory} timeout="auto" unmountOnExit>
          {[...history].reverse().map(h => (
            <Box key={h.id} className={classes.historyRow}>
              <Typography className={classes.historyId}>#{h.id}</Typography>
              <Typography className={classes.historyRev}>
                {h.revision?.substring(0, 8) ?? '-'}
              </Typography>
              <Typography className={classes.historyTime}>
                {formatTime(h.deployedAt)}
              </Typography>
              <Box flex={1} />
              <Typography className={classes.historyTime}>
                {formatTimeAgo(h.deployedAt)}
              </Typography>
            </Box>
          ))}
          {history.length === 0 && (
            <Box p={2}>
              <Typography variant="body2" color="textSecondary">배포 이력 없음</Typography>
            </Box>
          )}
        </Collapse>
      </Paper>

      {/* ─── Status Bar ─── */}
      <Box className={classes.statusBar}>
        <Typography variant="caption">
          {resources.length}개 리소스 · {history.length}건 배포
        </Typography>
        <Box display="flex" alignItems="center" style={{ gap: 8 }}>
          {autoRefresh && (
            <Chip
              size="small"
              icon={<PlayArrowIcon style={{ fontSize: 14 }} />}
              label="Live"
              color="primary"
              style={{ height: 20 }}
            />
          )}
          {loading && <CircularProgress size={12} />}
        </Box>
      </Box>
    </Box>
  );
};
